import { IX_TECHNOLOGIES } from "@constants/global.constants";

import { EMPTY_STATE_STRINGS } from "@utils/StringConstants";

import {
  DeleteIcon,
  DeleteProjectEmptyStateIcon,
  DuplicateIcon,
  EmptyShareByMeProjectsIcon,
  EmptyShareWithMeProjectsIcon,
  FavEmptyStateIcon,
  FolderEmptyStateIcon,
  FolderProjectEmptyStateIcon,
  ImportedProjectsIcon,
  ImportProjectEmptyStateIcon,
  OpenIcon,
  PermanentDeleteIcon,
  ProjectCollaboratorsIcon,
  ProjectEmptyStateIcon,
  RemoveFavoriteIcon,
  RemoveFromFolderIcon,
  RenameIcon,
  RestoreIcon,
  RestoreProjectIcon,
  SearchProjectEmptyState,
  SendProjectIcon,
  StarIcon,
  UnlockIcon,
} from "@components/SVGs";
import HomeFolderIcon from "@components/SVGs/HomeFolderIcon";

const IX_THUMBNAIL = "/images/Thumbnail_IX.svg";
const IX_TECH_IMAGES = Object.fromEntries(IX_TECHNOLOGIES.map(technology => [technology, IX_THUMBNAIL]));

export const TechImages = {
  UF: "/images/Thumbnail_UF.svg",
  ...IX_TECH_IMAGES,
  RO: "/images/Thumbnail_RO.svg",
  CCRO: "/images/Thumbnail_CCRO.svg",
};

export const SharedProjectTabsIndexs = {
  0: "sharedWithMe",
  1: "sharedByMe",
};

export const PROJECT_SORT_CONFIG = {
  name: { key: "projectName", type: "string", useNumeric: true },
  createdDate: { key: "createDate", type: "date" },
  modifiedDate: { key: "lastModified", type: "date" },
};

export const EMPTY_STATE_CONFIG = t => {
  const {
    all_projects,
    favourite_projects,
    imported_projects,
    deleted_items,
    folders,
    search_items,
    search_items_folder,
    folderProjectNotFound,
    search_deleted_items,
    projects_share_by_me,
    projects_share_with_me,
  } = EMPTY_STATE_STRINGS(t);

  return {
    home: {
      title: all_projects.title,
      description: all_projects.description,
      icon: ProjectEmptyStateIcon,
      buttonText: all_projects.buttonText,
    },
    all_projects: {
      title: all_projects.title,
      description: all_projects.description,
      icon: ProjectEmptyStateIcon,
      buttonText: all_projects.buttonText,
    },
    favourite_projects: {
      title: favourite_projects.title,
      description: favourite_projects.description,
      icon: FavEmptyStateIcon,
    },
    projects_shared_by_me: {
      title: projects_share_by_me.title,
      description: projects_share_by_me.description,
      icon: EmptyShareByMeProjectsIcon,
    },
    projects_shared_with_me: {
      title: projects_share_with_me.title,
      description: projects_share_with_me.description,
      icon: EmptyShareWithMeProjectsIcon,
    },
    imported_projects: {
      title: imported_projects.title,
      description: imported_projects.description,
      icon: ImportProjectEmptyStateIcon,
      buttonText: imported_projects.buttonText,
    },
    deleted_items: {
      title: deleted_items.title,
      description: deleted_items.description,
      icon: DeleteProjectEmptyStateIcon,
    },
    folders: {
      title: folders.title,
      description: folders.description,
      icon: FolderEmptyStateIcon,
      buttonText: folders.buttonText,
    },
    search_items: {
      title: search_items.title,
      description: search_items.description,
      icon: SearchProjectEmptyState,
    },
    search_items_folder: {
      title: search_items_folder.title,
      description: search_items_folder.description,
      icon: SearchProjectEmptyState,
    },
    folder_projects_not_found: {
      title: folderProjectNotFound.title,
      description: folderProjectNotFound.description,
      icon: FolderProjectEmptyStateIcon,
    },
    search_deleted_items: {
      title: search_deleted_items.title,
      description: search_deleted_items.description,
      icon: SearchProjectEmptyState,
    },
  };
};

export const PROJECT_ACTIONS = {
  OPEN: "openProject",
  SEND: "sendProject",
  DELETE: "deleteProject",
  RENAME: "renameProject",
  EXPORT: "exportProject",
  UNLOCK: "unlockProject",
  RESTORE: "restoreProject",
  ADD_TO_FAV: "addToFavorite",
  ADD_TO_FOLDER: "addToFolder",
  DUPLICATE: "duplicateProject",
  RESTORE_FOLDER: "restoreFolder",
  COLLABORATORES: "collaborators",
  REMOVE_FROM_FAV: "removeFromFavorite",
  REMOVE_FROM_FOLDER: "removeFromFolder",
  PERMANENT_DELETE: "permanentDeleteProject",
  PERMANENT_DELETE_FOLDER: "permanentDeleteFolder",
  EXAMPLE_PROJECTS: "exampleProjects",
};

export const FOLDER_ACTIONS = {
  OPEN: "openFolder",
  DELETE: "deleteFolder",
  RENAME: "renameFolder",
};

export const HOME_ACTIONS = {
  ACCOUNT_PREFERENCES: "accountPreferences",
  TERMS_AND_CONDITIONS: "termsAndConditions",
  CREATE_PROJECT: "createProject",
  IMPORT_PROJECT: "importProject",
  CREATE_FOLDER: "createFolder",
};

export const DELETED_FOLDER_MENUS = t => [
  {
    id: PROJECT_ACTIONS.RESTORE_FOLDER,
    label: t("projectMenu.restoreFolder"),
    icon: <RestoreIcon />,
    groupId: 1,
  },
  {
    id: PROJECT_ACTIONS.PERMANENT_DELETE_FOLDER,
    label: t("projectMenu.permanentlyDelete"),
    icon: <PermanentDeleteIcon />,
    groupId: 2,
    color: "var(--du-color-red)",
  },
];

export const DELETED_PROJECT_MENUS = t => [
  {
    id: PROJECT_ACTIONS.RESTORE,
    label: t("projectMenu.restoreProject"),
    icon: <RestoreProjectIcon />,
    groupId: 1,
  },
  {
    id: PROJECT_ACTIONS.PERMANENT_DELETE,
    label: t("projectMenu.permanentlyDelete"),
    icon: <PermanentDeleteIcon />,
    groupId: 2,
    color: "var(--du-color-red)",
  },
];

export const COLLABORATOS_PROJECT_MENUS = t => [
  {
    id: PROJECT_ACTIONS.OPEN,
    label: t("projectMenu.openProject"),
    icon: <OpenIcon />,
  },
  {
    id: PROJECT_ACTIONS.DUPLICATE,
    label: t("projectMenu.duplicateProject"),
    icon: <DuplicateIcon />,
  },
  {
    id: PROJECT_ACTIONS.COLLABORATORES,
    label: t("addCollaborators"),
    icon: <ProjectCollaboratorsIcon fill='var(--du-color-icon)' size={14} />,
  },
  {
    id: PROJECT_ACTIONS.UNLOCK,
    label: t("projectMenu.unlockProject"),
    icon: <UnlockIcon />,
  },
];

export const PROJECT_MENUS = t => [
  {
    id: PROJECT_ACTIONS.OPEN,
    label: t("projectMenu.openProject"),
    groupId: 1,
    icon: <OpenIcon />,
  },
  {
    id: PROJECT_ACTIONS.ADD_TO_FAV,
    label: t("projectMenu.addToFavorite"),
    groupId: 1,
    icon: <StarIcon />,
  },
  {
    id: PROJECT_ACTIONS.REMOVE_FROM_FAV,
    label: t("projectMenu.removeFromFavorite"),
    groupId: 1,
    icon: <RemoveFavoriteIcon />,
  },
  {
    id: PROJECT_ACTIONS.ADD_TO_FOLDER,
    label: t("projectMenu.moveToFolder"),
    groupId: 1,
    icon: <HomeFolderIcon fill='var(--du-color-icon)' size={14} />,
  },
  {
    id: PROJECT_ACTIONS.REMOVE_FROM_FOLDER,
    label: t("projectMenu.removeFromFolder"),
    groupId: 1,
    icon: <RemoveFromFolderIcon />,
  },
  {
    id: PROJECT_ACTIONS.DUPLICATE,
    label: t("projectMenu.duplicateProject"),
    groupId: 1,
    icon: <DuplicateIcon />,
  },
  {
    id: PROJECT_ACTIONS.COLLABORATORES,
    label: t("projectMenu.addCollaborators"),
    groupId: 1,
    icon: <ProjectCollaboratorsIcon fill='var(--du-color-icon)' size={14} />,
  },

  {
    id: PROJECT_ACTIONS.RENAME,
    label: t("projectMenu.renameProject"),
    groupId: 1,
    icon: <RenameIcon />,
  },
  {
    id: PROJECT_ACTIONS.UNLOCK,
    label: t("projectMenu.unlockProject"),
    groupId: 1,
    icon: <UnlockIcon />,
  },

  {
    id: PROJECT_ACTIONS.EXPORT,
    label: t("projectMenu.exportProject"),
    groupId: 2,
    icon: <ImportedProjectsIcon />,
  },
  {
    id: PROJECT_ACTIONS.SEND,
    label: t("projectMenu.sendProject"),
    groupId: 2,
    icon: <SendProjectIcon fill='var(--du-color-icon)' size={14} />,
  },

  {
    id: PROJECT_ACTIONS.DELETE,
    label: t("projectMenu.deleteProject"),
    groupId: 3,
    color: "var(--du-color-red)",
    icon: <DeleteIcon fill='var(--du-color-red)' width={14} height={14} />,
  },
];

export const FOLDER_MENUS = t => [
  {
    id: FOLDER_ACTIONS.OPEN,
    label: t("projectMenu.openProject"),
    icon: <OpenIcon />,
    groupId: 1,
  },
  {
    id: FOLDER_ACTIONS.RENAME,
    label: t("projectMenu.renameProject"),
    icon: <RenameIcon />,
    groupId: 1,
  },
  {
    id: FOLDER_ACTIONS.DELETE,
    label: t("projectMenu.deleteProject"),
    color: "var(--du-color-red)",
    icon: <DeleteIcon fill='var(--du-color-red)' width={14} height={14} />,
    groupId: 2,
  },
];

export const RECORDS_PER_PAGE = 20;
